<template>
  <div>
    <main-header>
      <v-spacer />
      <v-btn
        :loading="loadingNewProviderRate"
        color="primary"
        class="ma-2 hidden-sm-and-down"
        dark
        @click="onShowNewProviderRate"
      >
        New Provider Rate
        <v-icon right>
          mdi-plus
        </v-icon>
      </v-btn>
    </main-header>

    <v-container fluid>
      <v-data-table
        :headers="providerRatesTableHeaders"
        :items="itemsList"
        class="elevation-1 table"
        multi-sort
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :loading="loadingTable"
        hide-default-footer
        item-key="key"
        :items-per-page="perPage"
        fixed-header
        @update:sort-by="getItemsListWithSort"
        @update:sort-desc="getItemsListWithSort"
      >
        <template v-slot:top>
          <v-toolbar
            class="toolbar"
            height="84"
            flat
          >
            <div class="toolbar-content-special d-flex align-center w-100">
              <SelectPartners
                v-if="payoutPartnerName && partnersList && partnersList.length"
                :partners-list="partnersList"
                :partner-name="payoutPartnerName"
                @on-change-partner="onChangedSelectedPartner"
              />
              <v-spacer />
              <FilterInput
                v-model="filter"
                :items="providerRatesTableFilters"
                filter-range-from-payload-string=">"
                filter-range-to-payload-string="<"
              />
            </div>
          </v-toolbar>
          <v-divider />
        </template>

        <template v-slot:item.rate="{ item }">
          <CalcEndUserCell
            :calc-end-user-rate="item.rate"
            :rate-ranges="item.rateRanges"
          />
        </template>

        <template v-slot:item.dueDate="{ item }">
          <span v-if="item.dueDate">
            {{ item.dueDate | moment("DD-MM-YYYY") }}
            <v-chip
              v-if="item.expired"
              class="ml-1"
              color="red"
              text-color="white"
              x-small
            >
              Expired
            </v-chip>
          </span>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex">
            <v-icon
              small
              class="mr-2"
              @click="() => onOpenArchive(item)"
            >
              mdi-archive-clock
            </v-icon>
            <v-icon
              small
              class="mr-2"
              @click="() => onShowEditDialog(item)"
            >
              mdi-pencil
            </v-icon>
            <v-icon
              small
              class="mr-2"
              @click="() => onShowDeleteDialog(item)"
            >
              mdi-trash-can
            </v-icon>
          </div>
        </template>

        <template v-slot:no-data>
          <v-btn
            color="primary"
            dark
            @click="getItemsList"
          >
            Refresh
          </v-btn>
        </template>

        <template v-slot:footer>
          <v-container
            fluid
            class="mt-4"
          >
            <v-row>
              <v-col
                cols="9"
                align-self="center"
              >
                <v-pagination
                  v-model="page"
                  class="b-pagination"
                  :length="totalPages"
                  :total-visible="11"
                  @input="onChangePage"
                />
              </v-col>
              <v-col cols="3">
                <v-select
                  v-model="perPage"
                  class="select-per-page"
                  :items="perPageItems"
                  placeholder="Items per page"
                  dance
                  @change="perPage = parseInt($event, 10)"
                />
              </v-col>
            </v-row>
          </v-container>
          <div class="text-center py-2" />
        </template>
      </v-data-table>

      <ModalCreateProviderRate
        v-if="showCreateProviderRateDialog"
        v-model="showCreateProviderRateDialog"
        :selected-partner="payoutPartnerName"
        :source-currency="baseCurrency"
        @on-save="getItemsList"
      />

      <ModalEditProviderRate
        v-if="showEditProviderRateDialog"
        v-model="showEditProviderRateDialog"
        :data="editedItem"
        @save="getItemsList"
      />

      <ModalDeleteProviderRate
        v-if="showDeleteProviderRateDialog"
        v-model="showDeleteProviderRateDialog"
        :data="deletedItem"
        @delete="getItemsList"
      />
    </v-container>
  </div>
</template>

<script>
// constants
import {
  providerRatesTableHeaders,
  providerRatesTableFilters
} from "@/utils/staticTablesColumns";
// modules
import { mapGetters } from "vuex";
// components
import MainHeader from "@/components/MainHeader";
import FilterInput from "@/components/FilterInput";
import SelectPartners from "@/components/SelectPartners";
import ModalEditProviderRate from "@/components/modals/ModalEditProviderRate";
import ModalCreateProviderRate from "@/components/modals/ModalCreateProviderRate";
import ModalDeleteProviderRate from "@/components/modals/ModalDeleteProviderRate";
import CalcEndUserCell from "@/components/CalcEndUserCell";

export default {
  name: "ProviderRates",

  components: {
    MainHeader,
    FilterInput,
    SelectPartners,
    ModalCreateProviderRate,
    ModalEditProviderRate,
    ModalDeleteProviderRate,
    CalcEndUserCell
  },

  data() {
    return {
      providerRatesTableHeaders: providerRatesTableHeaders,
      providerRatesTableFilters: providerRatesTableFilters,
      payoutPartnerName: null,
      selectedPartner: {},
      editedItem: {},
      deletedItem: {},
      baseCurrency: null,
      showEditProviderRateDialog: false,
      showDeleteProviderRateDialog: false,
      showCreateProviderRateDialog: false,
      loadingNewProviderRate: false,

      restoreFromQuery: false,
      sortBy: [],
      sortDesc: [],
      loadingTable: false,
      perPage: 10,
      page: 1,
      filter: [],
      perPageItems: [10, 25, 50, 100, 500]
    }
  },

  computed: {
    ...mapGetters({
      itemsList: "ProviderRatesStore/providerRates",
      pagination: "ProviderRatesStore/providerRatesPagination",
      partnersList: "ProviderRatesStore/partnersList"
    }),

    totalPages() {
      return this.pagination.totalPages || 0;
    },

    filterStringPayload() {
      const filterObject = {};

      this.filter.forEach(item => {
        return filterObject[item.key] = item.value;
      });
      return filterObject
    },

    filterStringURL: {
      get() {
        return this.filter.map(item => {
          return `${item.key}:${item.value}:${item.name}`;
        }).join(",");
      },
      set(newValue) {
        const filterArray = newValue.split(",");

        filterArray.forEach((item, index) => {
          const filterKeyValue = item.split(":")
          this.filter[index] = {};
          this.filter[index].key = filterKeyValue[0];
          this.filter[index].value = filterKeyValue[1];
          this.filter[index].name = filterKeyValue[2] || "";
        });

        return newValue;
      }
    }
  },

  watch: {
    perPage (newValue) {
      if (this.restoreFromQuery) return;

      this.addQueryToRouteWithReload("perPage", newValue);
      this.addQueryToRouteWithReload("page", 1);
    },
    filter: {
      handler() {
        if (this.restoreFromQuery) return;

        this.getItemsListWithFilters();
      },
      deep: true
    }
  },

  created() {
    const query = this.$route.query;
    const hasQueryPayoutPartnerName = query.payoutPartnerName;

    if (hasQueryPayoutPartnerName) {
      this.payoutPartnerName = query.payoutPartnerName;
    }

    this.$store.dispatch("ProviderRatesStore/getPartners")
      .then((data) => {
        if (Array.isArray(data) && data.length) {
          let partner = data.find(item => item.name === this.payoutPartnerName);

          if (!partner) {
            partner = data[0];
          }

          if (!hasQueryPayoutPartnerName && partner) {
            this.payoutPartnerName = partner.name;
          }

          this.baseCurrency = partner?.baseCurrency || null;
        }

        this.getItemsList();
      })

    if (this.$route.query) {
      this.restoreFromQuery = true;
      const query = this.$route.query;
      this.perPage = parseInt(query.perPage) || 10;
      this.page = parseInt(query.page) || 1;
      let sort = query.sort || [];

      if (sort.length) {
        if (typeof sort === "string") sort = [sort];

        const sortBy = [];
        const sortDesc = [];
        sort.forEach(item => {
          const array = item.split(",");
          sortBy.push(array[0]);
          sortDesc.push(array[1] !== "asc");
        });
        this.sortBy = sortBy;
        this.sortDesc = sortDesc;
      }

      if (query?.filter?.length) {
        this.filterStringURL = query.filter;
      }

      if (query.payoutPartnerName) {
        this.payoutPartnerName = query.payoutPartnerName;
      }
    }
  },

  methods: {
    onOpenArchive (data) {
      const origin = window.location.origin;
      const pathname = '/audit-log-payout';
      const filter = `receiveCurrency:${ data.destinationCurrency }:Recieve currency`;
      const url = `${ origin }${ pathname }?payoutPartnerName=${ data.payoutPartnerName }&sendingCurrency=${ data.sourceCurrency }&filter=${ filter }`;

      return window.open(url);
    },

    onShowEditDialog (item) {
      this.editedItem = Object.assign({}, item);
      this.showEditProviderRateDialog = true;
    },

    onShowDeleteDialog (item) {
      this.deletedItem = Object.assign({}, item);
      this.showDeleteProviderRateDialog = true;
    },

    onShowNewProviderRate () {
      this.showCreateProviderRateDialog = true;
    },

    async getItemsList () {
      try {
        this.loadingTable = true;

        const payload = {
          payoutPartnerName: this.payoutPartnerName,
          page: this.page,
          size: this.perPage,
          sort: this.sortBy.map(
            (val, index) => `${val},${this.sortDesc[index] ? "desc" : "asc"}`
          ),
          ...this.filterStringPayload
        };

        await this.$store.dispatch("ProviderRatesStore/getProviderRates", payload)
      } finally {
        this.loadingTable = false;
        this.restoreFromQuery = false;
      }
    },

    getItemsListWithSort () {
      if (this.loadingTable) return;
      this.page = 1;
      let sort = this.sortBy.map((val, index) => {
        return `${val},${this.sortDesc[index] ? "desc" : "asc"}`;
      });
      this.addQueryToRouteWithReload("page", 1);
      this.addQueryToRouteWithReload("sort", sort);
    },

    getItemsListWithFilters () {
      this.addQueryToRouteWithReload("filter", this.filterStringURL);
      this.addQueryToRouteWithReload("perPage", this.perPage);
      this.addQueryToRouteWithReload("page", 1);
    },

    onChangedSelectedPartner (value) {
      const partnerName = value.name;
      const baseCurrency = value.baseCurrency;

      this.addQueryToRouteWithReload("payoutPartnerName", partnerName);
      this.addQueryToRouteWithReload("page", 1);
      this.payoutPartnerName = partnerName;
      this.baseCurrency = baseCurrency;
    },

    addQueryToRouteWithReload (name, value) {
      this.$router.replace({
        query: Object.assign({
          ...this.$route.query
        }, {
          [name]: value
        })},
      () => {}
      );
    },

    onChangePage () {
      this.addQueryToRouteWithReload("page", this.page);
    }
  }
}
</script>
